import React, { useCallback, useContext, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { Link, Icon } from "components/common";
import { Row } from "components/bootstrap";
import ViewPropertiesModal from "views/components/dashboard/DashboardPropertiesModal";
import onSaveView from "views/logic/views/OnSaveViewAction";
import View from "views/logic/views/View";
import Routes from "routing/Routes";
import useViewTitle from "views/hooks/useViewTitle";
import useView from "views/hooks/useView";
import useViewsDispatch from "views/stores/useViewsDispatch";
import FavoriteIcon from "views/components/FavoriteIcon";
import { updateView } from "views/logic/slices/viewSlice";
import useIsNew from "views/hooks/useIsNew";
import useGetPermissionsByScope from "hooks/useScopePermissions";
import { createGRN } from "logic/permissions/GRN";
import useAlertAndEventDefinitionData from "components/event-definitions/replay-search/hooks/useAlertAndEventDefinitionData";
import useReplaySearchContext from "components/event-definitions/replay-search/hooks/useReplaySearchContext";
import ViewsExecutionInfo from "views/components/views/ViewsExecutionInto";
import RightSidebarContext from "contexts/RightSidebarContext";
import SidebarEventDetails from "components/events/SidebarEventDetails";
import SidebarEventDefinitionDetails from "components/event-definitions/SidebarEventDefinitionDetails";
import Button from "components/bootstrap/Button";
const links = {
  [View.Type.Dashboard]: ({ id, title }) => [
    {
      link: Routes.DASHBOARDS,
      label: "Dashboards"
    },
    {
      label: title || id,
      dataTestId: "view-title"
    }
  ],
  [View.Type.Search]: ({ id, title }) => [
    {
      link: Routes.SEARCH,
      label: "Search"
    },
    {
      label: title || id,
      dataTestId: "view-title"
    }
  ],
  alert: ({ id, onShowDetails }) => [
    {
      link: Routes.ALERTS.LIST,
      label: "Alerts & Events"
    },
    {
      label: id,
      onClick: onShowDetails,
      dataTestId: "alert-id-title"
    }
  ],
  eventDefinition: ({ id, title, onShowDetails }) => [
    {
      link: Routes.ALERTS.DEFINITIONS.LIST,
      label: "Event definitions"
    },
    {
      label: title || id,
      onClick: onShowDetails,
      dataTestId: "event-definition-title"
    }
  ]
};
const Content = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    margin-bottom: ${theme.spacings.xs};
    gap: ${theme.spacings.sm};
    justify-content: space-between;
  `
);
const Breadcrumb = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: ${theme.spacings.xxs};
  `
);
const EditButton = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.gray[60]};
    font-size: ${theme.fonts.size.tiny};
    cursor: pointer;
  `
);
const TitleWrapper = styled.span`
  display: flex;
  gap: 4px;
  align-items: center;

  & ${EditButton} {
    display: none;
  }

  &:hover ${EditButton} {
    display: block;
  }
`;
const StyledIcon = styled(Icon)`
  font-size: 0.5rem;
`;
const CrumbLink = ({
  label,
  link,
  onClick = void 0,
  dataTestId = void 0
}) => {
  if (link) {
    return /* @__PURE__ */ React.createElement(Link, { target: "_blank", to: link, "data-testid": dataTestId }, label);
  }
  if (onClick) {
    return /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", onClick, "data-testid": dataTestId }, label);
  }
  return /* @__PURE__ */ React.createElement("span", { "data-testid": dataTestId }, label);
};
const ViewHeader = () => {
  var _a;
  const view = useView();
  const isNew = useIsNew();
  const isSavedView = (view == null ? void 0 : view.id) && (view == null ? void 0 : view.title) && !isNew;
  const { scopePermissions } = useGetPermissionsByScope({ _scope: view == null ? void 0 : view.scope });
  const [showMetadataEdit, setShowMetadataEdit] = useState(false);
  const toggleMetadataEdit = useCallback(() => setShowMetadataEdit((cur) => !cur), [setShowMetadataEdit]);
  const { alertId, definitionId, type } = useReplaySearchContext();
  const { definitionTitle } = useAlertAndEventDefinitionData(alertId, definitionId);
  const dispatch = useViewsDispatch();
  const rightSidebar = useContext(RightSidebarContext);
  const showAlertDetails = useCallback(
    () => rightSidebar == null ? void 0 : rightSidebar.openSidebar(SidebarEventDetails(alertId, definitionId)),
    [rightSidebar, alertId, definitionId]
  );
  const showEventDefinitionDetails = useCallback(
    () => rightSidebar == null ? void 0 : rightSidebar.openSidebar(SidebarEventDefinitionDetails(definitionId)),
    [rightSidebar, definitionId]
  );
  const _onSaveView = useCallback(
    async (updatedView) => {
      await dispatch(onSaveView(updatedView));
      await dispatch(updateView(updatedView));
    },
    [dispatch]
  );
  const typeText = (_a = view == null ? void 0 : view.type) == null ? void 0 : _a.toLocaleLowerCase();
  const title = useViewTitle();
  const onChangeFavorite = useCallback(
    (newValue) => dispatch(updateView(view.toBuilder().favorite(newValue).build())),
    [dispatch, view]
  );
  const canShowDetails = !!rightSidebar;
  const breadcrumbs = useMemo(() => {
    switch (type) {
      case "alert":
      case "event":
        return links.alert({ id: alertId, onShowDetails: canShowDetails ? showAlertDetails : void 0 });
      case "event_definition":
        return links.eventDefinition({
          id: definitionId,
          title: definitionTitle,
          onShowDetails: canShowDetails ? showEventDefinitionDetails : void 0
        });
      default:
        return links[view.type]({ id: view.id, title });
    }
  }, [
    type,
    alertId,
    canShowDetails,
    showAlertDetails,
    definitionId,
    definitionTitle,
    showEventDefinitionDetails,
    view.type,
    view.id,
    title
  ]);
  const showExecutionInfo = view.type === "SEARCH";
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Content, null, /* @__PURE__ */ React.createElement(Breadcrumb, null, breadcrumbs.map(({ label, link, onClick, dataTestId }, index) => {
    const theLast = index === breadcrumbs.length - 1;
    return /* @__PURE__ */ React.createElement(TitleWrapper, { key: `${label}_${link}` }, /* @__PURE__ */ React.createElement(CrumbLink, { link, label, onClick, dataTestId }), !theLast && /* @__PURE__ */ React.createElement(StyledIcon, { name: "chevron_right" }), isSavedView && theLast && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      FavoriteIcon,
      {
        isFavorite: view.favorite,
        grn: createGRN(view.type, view.id),
        onChange: onChangeFavorite
      }
    ), (scopePermissions == null ? void 0 : scopePermissions.is_mutable) && /* @__PURE__ */ React.createElement(
      EditButton,
      {
        onClick: toggleMetadataEdit,
        role: "button",
        title: `Edit ${typeText} ${view.title} metadata`,
        tabIndex: 0
      },
      /* @__PURE__ */ React.createElement(Icon, { name: "edit_square" })
    )));
  }), showMetadataEdit && /* @__PURE__ */ React.createElement(
    ViewPropertiesModal,
    {
      show: true,
      view,
      title: `Editing saved ${typeText}`,
      onClose: toggleMetadataEdit,
      onSave: _onSaveView,
      submitButtonText: `Save ${typeText}`
    }
  )), showExecutionInfo && /* @__PURE__ */ React.createElement(ViewsExecutionInfo, null)));
};
export default ViewHeader;
